import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { apiErrorMessage } from "../lib/utils";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { CurrencySelector } from "../components/LocaleSwitcher";
import { PhoneDialog } from "../components/PhoneDialog";
import { useLocale } from "../context/LocaleContext";
import {
  Check, Zap, Star, Crown, Plus, RefreshCw, History,
  Film, Shuffle, FlaskConical, PenLine, Video, Sparkles, RotateCcw, Lock, XCircle, ShieldCheck, CreditCard, CalendarClock,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const CREDIT_VALUE_USD = 0.20;
const CUSTOM_MIN = 25;

const PLAN_META = {
  starter: { icon: Zap, color: "#60a5fa" },
  growth: { icon: Star, color: "#c084fc" },
  pro: { icon: Crown, color: "#fbbf24" },
};

const ACTION_META = [
  { key: "concepts", icon: Sparkles, color: "#60a5fa" },
  { key: "fullAd", icon: Film, color: "#c084fc" },
  { key: "newVideo", icon: Video, color: "#f59e0b" },
  { key: "remix", icon: RotateCcw, color: "#94a3b8" },
  { key: "styles", icon: FlaskConical, color: "#34d399" },
  { key: "adjust", icon: PenLine, color: "#fb7185" },
  { key: "adjustRegen", icon: Shuffle, color: "#a78bfa" },
];

export default function Billing() {
  const { t, i18n } = useTranslation();
  const { formatPrice, formatAmount, currency } = useLocale();
  const [plans, setPlans] = useState([]);
  const [topups, setTopups] = useState([]);
  const [userBilling, setUserBilling] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customCredits, setCustomCredits] = useState("");
  const [subBusy, setSubBusy] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [pendingCheckout, setPendingCheckout] = useState(null);

  const loadBilling = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/billing/plans`, { withCredentials: true }),
      axios.get(`${API}/billing/credits`, { withCredentials: true }),
      axios.get(`${API}/billing/transactions`, { withCredentials: true }),
    ]).then(([plansRes, creditsRes, txnRes]) => {
      setPlans(plansRes.data.plans);
      setTopups(plansRes.data.topup_packs);
      setUserBilling(creditsRes.data);
      setTransactions(txnRes.data.transactions || []);
    }).catch(() => toast.error(t("billing.toasts.loadFailed")))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadBilling(); }, []); // eslint-disable-line

  const describeError = (err, fallback) => {
    const d = err.response?.data?.detail;
    if (d === "payments_not_configured") return t("billing.checkout.notConfigured");
    return apiErrorMessage(err, fallback);
  };

  // Hosted checkout: POST → { checkout_url } → hand the browser to Tap. Credits land after the webhook confirms.
  const runCheckout = async ({ endpoint, body, key }) => {
    setBusyKey(key);
    try {
      const res = await axios.post(`${API}${endpoint}`, { ...body, currency }, { withCredentials: true });
      toast.message(t("billing.checkout.redirecting"));
      window.location.assign(res.data.checkout_url);
    } catch (err) {
      if (err.response?.data?.detail === "phone_required") {
        setPendingCheckout({ endpoint, body, key });
        setPhoneOpen(true);
        return;
      }
      toast.error(describeError(err, t("billing.checkout.failed")));
    } finally { setBusyKey(null); }
  };

  const startCheckout = (opts, confirmMsg) => {
    if (busyKey) return;
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    if (!userBilling?.phone?.number) {
      setPendingCheckout(opts);
      setPhoneOpen(true);
      return;
    }
    runCheckout(opts);
  };

  const savePhone = async (phone) => {
    setPhoneBusy(true);
    try {
      const res = await axios.post(`${API}/billing/phone`, phone, { withCredentials: true });
      setUserBilling(b => ({ ...b, phone: res.data.phone }));
      setPhoneOpen(false);
      toast.success(t("billing.toasts.phoneSaved"));
      if (pendingCheckout) { const next = pendingCheckout; setPendingCheckout(null); runCheckout(next); }
    } catch (err) {
      toast.error(describeError(err, t("billing.phone.invalid")));
    } finally { setPhoneBusy(false); }
  };

  const planLabel = (id, fallback) => t(`billing.plans.labels.${id}`, { defaultValue: fallback || id });

  const handleSubscribe = (planId) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;
    const isSwitch = userBilling?.subscription_status === "active";
    const msg = t(isSwitch ? "billing.checkout.switch" : "billing.checkout.subscribe", {
      plan: planLabel(plan.id, plan.label), price: formatPrice(plan.price, { compact: true }), credits: plan.monthly_credits,
    });
    startCheckout({ endpoint: "/billing/subscribe", body: { plan_id: planId }, key: `plan-${planId}` }, msg);
  };

  const handleTopup = (pack) => {
    const msg = t("billing.checkout.topup", { count: pack.credits, price: formatPrice(pack.price, { compact: true }) });
    startCheckout({ endpoint: "/billing/topup", body: { pack_id: pack.id }, key: `topup-${pack.id}` }, msg);
  };

  const handleCustomTopup = () => {
    const count = parseInt(customCredits) || 0;
    if (count < CUSTOM_MIN) { toast.error(t("billing.topups.minCredits", { count: CUSTOM_MIN })); return; }
    const msg = t("billing.checkout.topup", { count, price: formatPrice(count * CREDIT_VALUE_USD) });
    startCheckout({ endpoint: "/billing/topup-custom", body: { credits: count }, key: "custom" }, msg);
  };

  const handleCancel = async () => {
    if (subBusy) return;
    if (!window.confirm(t("billing.confirm.cancel"))) return;
    setSubBusy(true);
    try {
      const res = await axios.post(`${API}/billing/cancel-subscription`, {}, { withCredentials: true });
      toast.success(res.data.message || t("billing.toasts.canceled"));
      loadBilling();
    } catch (err) {
      toast.error(apiErrorMessage(err, t("billing.toasts.cancelFailed")));
    } finally { setSubBusy(false); }
  };

  if (loading) return (
    <Layout>
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 text-purple-400 animate-spin" />
      </div>
    </Layout>
  );

  const activePlan = userBilling?.subscription_plan;
  const subStatus = userBilling?.subscription_status;
  const isActive = subStatus === "active";
  const customCount = parseInt(customCredits) || 0;
  const perCredit = formatPrice(CREDIT_VALUE_USD);
  const expiresAt = userBilling?.subscription_expires_at ? new Date(userBilling.subscription_expires_at) : null;
  const expiresLabel = expiresAt ? expiresAt.toLocaleDateString(i18n.language, { day: "numeric", month: "short", year: "numeric" }) : "";
  const isTestMode = userBilling?.payments && !userBilling.payments.live;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-10">

        {/* Header */}
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h1 className="text-3xl font-black text-white mb-1">{t("billing.title")}</h1>
            <p className="text-sm" style={{ color: "#6b6b6b" }}>{t("billing.subtitle")}</p>
          </div>
          <div className="text-right">
            <div className="text-5xl font-black text-white leading-none" data-testid="credit-balance">
              {userBilling?.credits ?? 0}
            </div>
            <div className="text-xs mt-1" style={{ color: "#6b6b6b" }}>{t("billing.creditsAvailable")}</div>
            {activePlan && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold"
                style={{
                  background: isActive ? "rgba(168,85,247,0.1)" : "rgba(248,113,113,0.08)",
                  border: `1px solid ${isActive ? "rgba(168,85,247,0.2)" : "rgba(248,113,113,0.2)"}`,
                  color: isActive ? "#c084fc" : "#f87171"
                }}
                data-testid="subscription-status-pill">
                {isActive
                  ? t("billing.statusActive", { plan: planLabel(activePlan, userBilling.plan_label), credits: userBilling.monthly_credits })
                  : t("billing.statusCanceled", { plan: planLabel(activePlan, userBilling.plan_label) })}
              </div>
            )}
            {isActive && expiresAt && (
              <div className="mt-2 flex items-center justify-end gap-1.5 text-xs" style={{ color: "#8a8a8a" }} data-testid="subscription-renewal-info">
                <CalendarClock className="w-3.5 h-3.5" />
                {userBilling.auto_renew ? t("billing.renewsOn", { date: expiresLabel }) : t("billing.endsOn", { date: expiresLabel })}
              </div>
            )}
            {userBilling?.card?.last4 && (
              <div className="mt-1 flex items-center justify-end gap-1.5 text-xs" style={{ color: "#8a8a8a" }} data-testid="card-on-file">
                <CreditCard className="w-3.5 h-3.5" />
                {t("billing.cardOnFile", { brand: userBilling.card.brand || "Card", last4: userBilling.card.last4 })}
              </div>
            )}
          </div>
        </div>

        <div className="mb-10 flex flex-wrap items-center justify-between gap-3">
          <CurrencySelector />
          <div className="flex items-center gap-3">
            {isTestMode && (
              <span className="text-[11px] font-bold px-2.5 py-1 rounded-full" data-testid="payments-test-mode-badge"
                style={{ background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.3)", color: "#fbbf24" }}>
                {t("billing.testMode")}
              </span>
            )}
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs" style={{ color: "#6b6b6b" }} data-testid="secure-payments-note">
              <ShieldCheck className="w-3.5 h-3.5" style={{ color: "#86efac" }} /> {t("billing.securePayments")}
            </span>
          </div>
        </div>

        {!isActive && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl px-5 py-4 mb-8 flex items-start gap-3"
            style={{ background: "rgba(168,85,247,0.07)", border: "1px solid rgba(168,85,247,0.25)" }}
            data-testid="subscription-required-banner">
            <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#c084fc" }} />
            <div className="flex-1">
              <p className="text-sm font-bold text-white">
                {subStatus === "canceled" ? t("billing.gate.canceledTitle") : t("billing.gate.requiredTitle")}
              </p>
              <p className="text-xs mt-1" style={{ color: "#a0a0a0" }}>
                {subStatus === "canceled"
                  ? t("billing.gate.canceledBody", { credits: userBilling?.credits ?? 0 })
                  : t("billing.gate.requiredBody")}
              </p>
            </div>
          </motion.div>
        )}

        {/* Plans */}
        <section className="mb-12">
          <h2 className="text-base font-bold text-white mb-1">{t("billing.plans.title")}</h2>
          <p className="text-sm mb-6" style={{ color: "#6b6b6b" }}>{t("billing.plans.subtitle")}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((plan, i) => {
              const meta = PLAN_META[plan.id] || PLAN_META.starter;
              const Icon = meta.icon;
              const color = meta.color;
              const isCurrent = activePlan === plan.id;
              const perks = t(`billing.plans.perks.${plan.id}`, { returnObjects: true });
              return (
                <motion.div key={plan.id}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                  className="relative rounded-2xl p-6 flex flex-col"
                  data-testid={`plan-card-${plan.id}`}
                  style={{
                    background: plan.popular ? "rgba(124,58,237,0.07)" : "#161616",
                    border: `1px solid ${plan.popular ? "#7c3aed" : "#252525"}`,
                  }}>
                  {plan.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold px-3 py-1 rounded-full"
                      style={{ background: "#7c3aed", color: "white" }}>{t("billing.plans.mostPopular")}</span>
                  )}

                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}18` }}>
                      <Icon className="w-4 h-4" style={{ color }} />
                    </div>
                    <span className="font-bold text-white">{t(`billing.plans.labels.${plan.id}`, { defaultValue: plan.label })}</span>
                  </div>

                  <div className="mb-5">
                    <span className="text-4xl font-black text-white" data-testid={`plan-price-${plan.id}`}>{formatPrice(plan.price, { compact: true })}</span>
                    <span className="text-sm ml-1" style={{ color: "#6b6b6b" }}>{t("billing.plans.perMonth")}</span>
                    <div className="mt-1 text-sm font-semibold" style={{ color }}>
                      {t("billing.plans.creditsIncluded", { count: plan.monthly_credits })}
                    </div>
                  </div>

                  <ul className="space-y-2.5 mb-6 flex-1">
                    {perks.map(perk => (
                      <li key={perk} className="flex items-start gap-2 text-sm" style={{ color: "#7a7a7a" }}>
                        <Check className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color }} />
                        {perk}
                      </li>
                    ))}
                  </ul>

                  {isActive && isCurrent ? (
                    <div className="flex flex-col gap-2">
                      <div className="w-full py-3 rounded-xl text-center text-sm font-bold"
                        style={{ background: "rgba(168,85,247,0.08)", border: "1px solid rgba(168,85,247,0.2)", color: "#c084fc" }}
                        data-testid={`subscribe-${plan.id}`}>
                        {t("billing.plans.currentPlan")}
                      </div>
                      <button onClick={handleCancel} disabled={subBusy}
                        data-testid="cancel-subscription-btn"
                        className="w-full py-2 rounded-xl text-center text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
                        style={{ background: "transparent", border: "1px solid #333", color: "#888" }}>
                        <XCircle className="w-3.5 h-3.5" /> {t("billing.plans.cancel")}
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => handleSubscribe(plan.id)} disabled={!!busyKey || subBusy}
                      data-testid={`subscribe-${plan.id}`}
                      className="w-full py-3 rounded-xl text-center text-sm font-bold flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
                      style={{
                        background: plan.popular ? "#7c3aed" : "#252525",
                        color: plan.popular ? "white" : "#a0a0a0",
                      }}>
                      {busyKey === `plan-${plan.id}` ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                      {isCurrent ? t("billing.plans.resubscribe") : (isActive ? t("billing.plans.switch") : t("billing.plans.subscribe"))}
                    </button>
                  )}
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Top-ups */}
        <section className="mb-12">
          <h2 className="text-base font-bold text-white mb-1">{t("billing.topups.title")}</h2>
          <p className="text-sm mb-5" style={{ color: "#6b6b6b" }}>
            {isActive ? t("billing.topups.subtitleActive") : t("billing.topups.subtitleLocked")}
          </p>

          {!isActive ? (
            <div className="rounded-2xl p-6 flex items-start gap-3"
              style={{ background: "#161616", border: "1px dashed #2a2a2a" }}
              data-testid="topup-locked-card">
              <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#6b6b6b" }} />
              <div>
                <p className="text-sm font-bold text-white">{t("billing.topups.lockedTitle")}</p>
                <p className="text-xs mt-1" style={{ color: "#6b6b6b" }}>{t("billing.topups.lockedBody")}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-5">
                {topups.map((pack, i) => (
                  <motion.div key={pack.id}
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + i * 0.04 }}
                    className="rounded-2xl p-5 flex items-center justify-between"
                    data-testid={`topup-card-${pack.id}`}
                    style={{ background: "#161616", border: "1px solid #252525" }}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: "rgba(168,85,247,0.1)" }}>
                        <Plus className="w-4 h-4 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-base font-black text-white">{t("common.creditsCount", { count: pack.credits })}</p>
                        {pack.savings_pct && <p className="text-xs font-bold" style={{ color: "#86efac" }}>{t("billing.topups.savings", { pct: pack.savings_pct })}</p>}
                      </div>
                    </div>
                    <button onClick={() => handleTopup(pack)} disabled={!!busyKey}
                      data-testid={`topup-${pack.id}`}
                      className="px-4 py-2 rounded-xl font-bold text-sm transition-opacity hover:opacity-75 disabled:opacity-50 flex items-center gap-1.5"
                      style={{ background: "rgba(124,58,237,0.12)", border: "1px solid rgba(124,58,237,0.25)", color: "#c084fc" }}>
                      {busyKey === `topup-${pack.id}` && <RefreshCw className="w-3 h-3 animate-spin" />}
                      {formatPrice(pack.price, { compact: true })}
                    </button>
                  </motion.div>
                ))}
              </div>

              <div className="rounded-2xl p-5" style={{ background: "#161616", border: "1px solid #252525" }}>
                <p className="text-sm font-bold text-white mb-1">{t("billing.topups.custom")}</p>
                <p className="text-xs mb-3" style={{ color: "#6b6b6b" }}>{t("billing.topups.minCredits", { count: CUSTOM_MIN })}</p>
                <div className="flex items-center gap-4">
                  <div className="flex-1 relative">
                    <input
                      type="number"
                      min={CUSTOM_MIN}
                      placeholder={t("billing.topups.enterCredits")}
                      value={customCredits}
                      onChange={e => setCustomCredits(e.target.value.replace(/[^0-9]/g, ""))}
                      data-testid="custom-credits-input"
                      className="w-full rounded-xl px-4 py-2.5 text-sm text-white font-bold outline-none"
                      style={{ background: "#0e0e0e", border: "1px solid #333", caretColor: "#c084fc" }}
                    />
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-2xl font-black" style={{ color: customCount > 0 ? "#fff" : "#333" }} data-testid="custom-credits-price">
                      {customCount > 0 ? formatPrice(customCount * CREDIT_VALUE_USD) : "—"}
                    </p>
                    <p className="text-xs" style={{ color: "#6b6b6b" }}>{t("billing.topups.perCredit", { price: perCredit })}</p>
                  </div>
                </div>
                {customCount > 0 && (
                  <button onClick={handleCustomTopup} disabled={!!busyKey || customCount < CUSTOM_MIN}
                    data-testid="custom-topup-btn"
                    className="mt-4 w-full py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-opacity hover:opacity-75 disabled:opacity-40"
                    style={{ background: "rgba(124,58,237,0.12)", border: "1px solid rgba(124,58,237,0.25)", color: "#c084fc" }}>
                    {busyKey === "custom" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
                    {customCount < CUSTOM_MIN
                      ? t("billing.topups.minCredits", { count: CUSTOM_MIN })
                      : t("billing.topups.getCredits", { count: customCount, price: formatPrice(customCount * CREDIT_VALUE_USD) })}
                  </button>
                )}
              </div>
            </>
          )}
        </section>

        {/* Action costs */}
        <section className="mb-12">
          <h2 className="text-base font-bold text-white mb-1">{t("billing.costs.title")}</h2>
          <p className="text-sm mb-5" style={{ color: "#6b6b6b" }}>{t("billing.costs.subtitle")}</p>
          <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid #252525" }}>
            {ACTION_META.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={item.key}
                  className="flex items-center justify-between px-5 py-4"
                  style={{ borderBottom: i < ACTION_META.length - 1 ? "1px solid #1a1a1a" : "none" }}>
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${item.color}12` }}>
                      <Icon className="w-3.5 h-3.5" style={{ color: item.color }} />
                    </div>
                    <div>
                      <p className="text-sm text-white">{t(`billing.costs.items.${item.key}.label`)}</p>
                      <p className="text-xs mt-0.5" style={{ color: "#6b6b6b" }}>{t(`billing.costs.items.${item.key}.note`)}</p>
                    </div>
                  </div>
                  <span className="text-sm font-bold font-mono whitespace-nowrap ml-4" style={{ color: item.color }}>
                    {t(`billing.costs.items.${item.key}.detail`)}
                  </span>
                </div>
              );
            })}
          </div>
        </section>

        {transactions.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-5">
              <History className="w-4 h-4 text-purple-400" />
              <h2 className="text-base font-bold text-white">{t("billing.history")}</h2>
            </div>
            <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid #252525" }}>
              {transactions.map((txn, i) => (
                <div key={txn.transaction_id || i}
                  data-testid={`txn-row-${i}`}
                  className="flex items-center justify-between px-5 py-4"
                  style={{ borderBottom: i < transactions.length - 1 ? "1px solid #1a1a1a" : "none" }}>
                  <div>
                    <p className="text-sm text-white">{t(`billing.txnTypes.${txn.type}`, { defaultValue: txn.type?.replace(/_/g, " ") })}</p>
                    <p className="text-xs" style={{ color: "#6b6b6b" }}>
                      {new Date(txn.created_at).toLocaleDateString(i18n.language)}
                      {txn.amount != null && txn.currency && <span dir="ltr"> · {formatAmount(txn.amount, txn.currency)}</span>}
                    </p>
                  </div>
                  <span className="text-sm font-bold" style={{ color: txn.credits > 0 ? "#86efac" : "#f87171" }}>
                    {txn.credits > 0 ? "+" : ""}{t("common.creditsCount", { count: txn.credits })}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

      </div>
      <PhoneDialog open={phoneOpen} onOpenChange={(o) => { setPhoneOpen(o); if (!o) setPendingCheckout(null); }}
        currency={currency} onSubmit={savePhone} busy={phoneBusy} />
    </Layout>
  );
}
